import React, { useState, useMemo, useRef } from 'react'
import { ApproverRequestDetails } from '../../../store/slices/Approver/approverRequestDetailsSlice'
import { decodeComment, getLatestStatusFields } from '../../../utils/statusFieldsUtils'
import { getButtonStateConfig } from '../ApprovalForms/buttonStateUtils'
import ApproverStatusRightPanel from '../StatusViews/ApproverStatusRightPanel'
import RequestDetailsActions from './RequestDetailsActions'
import { formatTimesDisplay, isRequestDateBeforeToday } from '../../../utils/date'
import StatusFieldsList from './StatusFieldsList'
import { formatDateSeries } from '../../../utils/dataTransformSeries'
import ValidationBanner from '../../../components/common/ValidationBanner'

interface RequestDetailsViewProps {
  data: ApproverRequestDetails | null
  /** Forwarded to the right panel's Print button so it prints only the request content. */
  onPrint?: () => void
  /**
   * Optional callback that receives approved-hours mapping from the Partial
   * Approve form. The container component (RequestProcessContainer) provides
   * this to lift state up so the single Current Request table can display
   * live approved hours.
   */
  onApprovedChange?: (payload: { byRow: { id: string; requestedHours: number; approvedHours: number }[]; totalApprovedHours: number }) => void
  /**
   * Invoked when the active form selection changes. Used by the container to
   * clear any transient approved-hours mapping so table values reset.
   */
  onActiveFormChange?: (form?: string) => void
  /**
   * Mapping of approved hours per request-day id provided by the container.
   * This is forwarded down to the PartiallyApprovedTimeOffForm so it can
   * initialize checkbox state when the user returns to that form.
   */
  approvedHoursById?: Record<string, number>
}

/**
 * RequestDetailsView component — orchestrates view and action components
 * based on current request status. Delegates to:
 * - RequestDetailsActions: renders action forms for editable statuses
 * - RequestDetailsStatus: renders read-only views for final statuses
 */
const RequestDetailsView: React.FC<RequestDetailsViewProps> = ({ data, onPrint, onApprovedChange, approvedHoursById, onActiveFormChange }) => {
  const [activeForm, setActiveForm] = useState<
    'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  >('approved')
  const isInitialMount = useRef(true)

  // Check if request dates are in the past (MUST be before early return)
  const isPastRequest = useMemo(
    () => isRequestDateBeforeToday(data?.startDate),
    [data?.startDate]
  )

  // Compute allDaysOverlap - TRUE if all requested dates have isOverlapped=true (MUST be before early return)
  const allDaysOverlap = useMemo(() => {
    if (!data?.requestedDates || data.requestedDates.length === 0) return false
    return data.requestedDates.every((d) => d.isOverlapped === true)
  }, [data?.requestedDates])

  // Compute button state configuration (MUST be before early return)
  const buttonStateConfig = useMemo(() => {
    if (!data) return null

    const currentStatus = data.statuses?.[0]?.status?.toUpperCase() || ''
    const isOnHold = [
      'SUBMITTED_ONHOLD',
      'RESUBMITTED_ONHOLD',
      'RTSUBMITTED_ONHOLD',
      'PRTSUBMITTED_ONHOLD',
    ].includes(currentStatus)

    const isRetractionFlow = [
      'RTSUBMITTED',
      'RTSUBMITTED_RELEASED',
      'RTSUBMITTED_ONHOLD',
    ].includes(currentStatus)

    const isPartialRetractionFlow = [
      'PRTSUBMITTED',
      'PRTSUBMITTED_RELEASED',
      'PRTSUBMITTED_ONHOLD',
    ].includes(currentStatus)

    return getButtonStateConfig({
      status: currentStatus,
      isPartialApprovalAllowed: data.isPartialApprovalAllowed,
      isFullApprovalAllowed: data.isFullApprovalAllowed,
      isHoldAllowed: data.isHoldAllowed,
      isOverrideAllowed: data.isOverrideAllowed,
      isOnHold,
      allDaysOverlap,
      isRetractionFlow,
      isPartialRetractionFlow,
      isPastRequest,
    })
  }, [data, allDaysOverlap, isPastRequest])

  // Build table rows from requestedDates (MUST be before early return - Rules of Hooks)
  const tableRows = useMemo(
    () =>
      (data?.requestedDates ?? []).map((d) => ({
        id: String(d.toReqDtlId),
        date: d.date,
        day: d.day || '',
        times: formatTimesDisplay(d.times),
        requestedHours: String(d.requestedHours),
        isOverlapped: d.isOverlapped,
      })),
    [data?.requestedDates]
  )

  // Compute current status (MUST be before early return - Rules of Hooks)
  const currentStatus = data?.statuses?.[0]?.status?.toUpperCase() || ''

  // Status type checks (MUST be before early return - Rules of Hooks)
  const isSubmittedRequest = [
    'SUBMITTED',
    'SUBMITTED_RELEASED',
    'RTSUBMITTED',
    'PRTSUBMITTED',
    'RTSUBMITTED_RELEASED',
    'RESUBMITTED',
    'RESUBMITTED_RELEASED',
  ].includes(currentStatus)
  const isOnHoldRequest = [
    'SUBMITTED_ONHOLD',
    'RESUBMITTED_ONHOLD',
    'RTSUBMITTED_ONHOLD',
    'PRTSUBMITTED_ONHOLD',
  ].includes(currentStatus)
  const isRetractionRequest = [
    'RTSUBMITTED',
    'RTSUBMITTED_RELEASED',
    'RTSUBMITTED_ONHOLD',
  ].includes(currentStatus)
  const isPartialRetractionRequest = [
    'PRTSUBMITTED',
    'PRTSUBMITTED_RELEASED',
    'PRTSUBMITTED_ONHOLD',
  ].includes(currentStatus)
  const isDeniedRequest = ['DENIED', 'PDENIED'].includes(
    currentStatus
  )
  const isOverrideDeniedRequest = [
    'RTDENIED',
    'PRTDENIED',
    'OVERRIDDEN_RTDENIED',
    'PAPPROVED_RTDENIED',
  ].includes(currentStatus)


  // Determine if status allows editing/actions (MUST be before early return - Rules of Hooks)
  const isEditableStatus =
    isSubmittedRequest ||
    isOnHoldRequest ||
    isRetractionRequest ||
    isPartialRetractionRequest ||
    (isDeniedRequest && !isPastRequest) ||
    isOverrideDeniedRequest

  // Auto-set form based on status on initial mount only (MUST be before early return - Rules of Hooks)
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false

      if (
        [
          'RTDENIED',
          'PRTDENIED',
          'RTDENIED_OVERRIDDEN',
          'OVERRIDDEN_RTDENIED',
          'TORPAPPROVEDRETRACTIONDENIED',
          'PAPPROVED_RTDENIED',
        ].includes(currentStatus)
      ) {
        setActiveForm('approved')
      }
      else if (
        ['DENIED', 'PDENIED'].includes(currentStatus) &&
        !isPastRequest
      ) {
        setActiveForm('denied')
      }
      else if (
        isOnHoldRequest &&
        !data?.isPartialApprovalAllowed &&
        !data?.isFullApprovalAllowed
      ) {
        // No approval permissions -> Returned should be selected
        setActiveForm('returned')
      }
      else if (isOnHoldRequest) {
        setActiveForm('hold')
      }
      else if (
        data?.isPartialApprovalAllowed &&
        !data?.isFullApprovalAllowed &&
        !buttonStateConfig?.partiallyApprovedDisabled
      ) {
        setActiveForm('partiallyapproved')
      }
      else {
        setActiveForm('approved')
      }
    }
  }, [
    data,
    currentStatus,
    isOnHoldRequest,
    isPastRequest,
    buttonStateConfig
  ])

  if (!data) {
    return <div className="text-center py-12">No request data available.</div>
  }


  // Right panel's "Submitted By" always reflects the original submission (regardless
  // of current status) — the resubmission/retraction actor is shown separately in the
  // status summary (getLatestStatusFields), so this must not be conflated with it.
  const getSubmittedBy = () => {
    // Check if the current status is 'SUBMITTED' in the statuses array
    const isCurrentStatusSubmitted = !!data.statuses?.find((item: any) => item.status === 'SUBMITTED')
    let submittedByFullName: string | null | undefined
    let submittedByNuid: string | null | undefined

    // If the current status is 'SUBMITTED', get the submitter from the statuses array;
    // otherwise, get it from the audit history.
    if (isCurrentStatusSubmitted) {
      const submittedStatus = data.statuses?.find((item: any) => item.status === 'SUBMITTED')
      submittedByFullName = submittedStatus?.fullName
      submittedByNuid = submittedStatus?.updatedBy
    } else {
      const submittedAudit = data.auditHistory?.find((item: any) => item.toffReqstStatus === 'SUBMITTED')
      submittedByFullName = submittedAudit?.fullName
      submittedByNuid = submittedAudit?.lastUpdatedBy
    }

    if (!submittedByFullName) return 'N/A'

    const submittedBy = `${submittedByFullName} (${submittedByNuid})`
    const actorNuid = submittedByNuid?.trim().toLowerCase()
    const ownerNuid = data.empNUID?.trim().toLowerCase()

    // Only label the original submitter a proxy when their nuid actually differs
    // from the request owner's — not merely because manualReqRcvdDate is set.
    return actorNuid && ownerNuid && actorNuid !== ownerNuid
      ? `Requestor Proxy ${submittedBy}`
      : submittedBy
  }


  // Build employee name
  const timeOffRequestFor = data.employee?.employeeName || 'N/A'
  const submittedBy = getSubmittedBy()

  // Employee submitted comment - checking in audit history
  const employeeCommentFromAuditHistory = decodeComment(
    data.auditHistory?.find((item) => item.toffReqstStatus === 'SUBMITTED')?.comments
  )

  // Employee submitted comment - check in statuses array (if present status is SUBMITTED)
  const employeeCommentFromStatus = decodeComment(
    data.statuses?.find((item) => item.status === 'SUBMITTED')?.comments
  )

  const employeeComment = employeeCommentFromAuditHistory || employeeCommentFromStatus

  // Fields summarizing the latest status change (e.g. Resubmitted On/By, Hold Placed On/By);
  // empty for a plain first submission since there is no prior approver activity to report
  const statusSummaryFields = getLatestStatusFields(
    data.statuses[0],
    data.partialRefId,
    data.manualReqRcvdDate,
    data.requestedDates.some((requestedDate) => requestedDate.requestType === 'A'),
    data.empNUID
  )

  // No action form renders for finalized/read-only statuses (and past-denied notices),
  // so the status summary shifts from below the row to flush-left, beside the panel
  const showsActionForm = isEditableStatus

  // Fetch requested dates for the time off request
  const requestedDateLabel = formatDateSeries(data.requestedDates.map((requestedDate) => requestedDate.date))

  return (
    <div className="w-full">


      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row items-start">
        {/* Left: Main Content (Forms or Status Views) */}
        <div className="flex-1">
          {/* PAST DENIED REQUEST NOTICE */}
          {isDeniedRequest && isPastRequest && (
            <div className="mb-6!">
              <ValidationBanner
                variant="info"
                heading="Request Denied (Past Request)"
                message="This request is for a past date and cannot be modified."
              />
            </div>
          )}

          {!showsActionForm && statusSummaryFields.length > 0 && (
            <div className="mb-6!">
              <StatusFieldsList fields={statusSummaryFields} />
            </div>
          )}

          {/* ACTIONS SECTION - an interactive form (status radios, comments,
              character counter), not part of the print-friendly view, so it's
              marked for removal from the printed clone via data-print-exclude. */}
          {isEditableStatus && buttonStateConfig && (
            <div data-print-exclude="true">
              <RequestDetailsActions
                data={data}
                tableRows={tableRows}
                isPastRequest={isPastRequest}
                isSubmittedRequest={isSubmittedRequest}
                isOnHoldRequest={isOnHoldRequest}
                isRetractionRequest={isRetractionRequest}
                isPartialRetractionRequest={isPartialRetractionRequest}
                isDeniedRequest={isDeniedRequest}
                isOverrideDeniedRequest={isOverrideDeniedRequest}
                activeForm={activeForm}
                setActiveForm={setActiveForm}
                buttonStateConfig={buttonStateConfig}
                onApprovedChange={onApprovedChange}
                onActiveFormChange={onActiveFormChange}
                approvedHoursById={approvedHoursById}
              />
            </div>
          )}
        </div>

        {/* Right: Request Details Panel */}
        <ApproverStatusRightPanel
          timeOffRequestFor={timeOffRequestFor}
          timeOffRequestDates={requestedDateLabel}
          timeOffRequestId={data.toReqId}
          submittedOn={data.createdTime}
          submittedBy={submittedBy}
          employeeComments={employeeComment}
          manualRequestedDate={data.manualReqRcvdDate}
          onPrint={onPrint}
        />
      </div>

      {/* Latest status summary (e.g. Resubmitted, Hold, Retraction Submitted, Override Denied) -
          rendered below the action form, indented to match the form's own left padding */}
      {showsActionForm && statusSummaryFields.length > 0 && (
        <div className="mb-6! px-6!">
          <StatusFieldsList fields={statusSummaryFields} />
        </div>
      )}
    </div>
  )
}

export default RequestDetailsView